import { Id, Lookbook, Navigation } from '@typings';

import { compiledPaths } from '../paths';
import { isDefined } from '../utils/is';
import { isEmpty } from '../utils/isEmpty';

const PAGES_URL = '/pages/';

export const getIsMenuItemActive = (link: string, pathname: string) => {
  const matchesExactLink = link === pathname;
  const matchesProductsPath = link === compiledPaths.PRODUCTS_ROOT({}) && pathname.includes(compiledPaths.PRODUCTS_ROOT({}));
  const matchesPagePath = link.includes(PAGES_URL) && pathname.includes(`${link}/`);

  return matchesExactLink || matchesProductsPath || matchesPagePath;
};

export const getHasActiveNavigationItemChild = (item: Navigation.Item, pathname: string): boolean => {
  if (item.type === 'link' || !isDefined(item.children) || isEmpty(item.children)) {
    return false;
  }

  return item.children.some(child => {
    if (child.type === 'folder') {
      return getHasActiveNavigationItemChild(child, pathname);
    }
    const isMenuItemActive = getIsMenuItemActive(child.url, pathname);

    if (child.type !== 'link' && isDefined(child.children) && !isEmpty(child.children)) {
      return getHasActiveNavigationItemChild(child, pathname) || isMenuItemActive;
    }

    return isMenuItemActive;
  });
};

export const mapLookbookNavigationItemToNavigationItem =
  (lookbookId: string, wishlistId: string | undefined) =>
  (item: Lookbook.NavigationItem): Navigation.Item => {
    // eslint-disable-next-line default-case
    switch (item.type) {
      case 'folder': {
        return {
          children: (item.children ?? []).map(mapLookbookNavigationItemToNavigationItem(lookbookId, wishlistId)),
          label: item.label,
          type: item.type,
        };
      }
      case 'link': {
        return {
          label: item.label,
          openNewTab: item.openNewTab,
          type: item.type,
          url: item.url,
        };
      }
      case 'page': {
        return {
          children: (item.children ?? []).map(mapLookbookNavigationItemToNavigationItem(lookbookId, wishlistId)),
          label: item.label,
          openNewTab: item.openNewTab,
          type: item.type,
          url: compiledPaths.LOOKBOOK_CMS_PAGE({ lookbookId, pageSlug: item.url, wishlistId }),
        };
      }
    }
  };

export const getHasActivePageChild = (item: Navigation.UserMenuItem | Lookbook.NavigationItem, pathname: string): boolean => {
  if (!isDefined(item.children) || isEmpty(item.children)) {
    return false;
  }

  return item.children.some(child => {
    const targetUrl = `${PAGES_URL}${child.url}`;
    const isMenuItemActive = getIsMenuItemActive(targetUrl, pathname);

    if (isDefined(child.children) && !isEmpty(child.children)) {
      return getHasActivePageChild(child, pathname) || isMenuItemActive;
    }

    return isMenuItemActive;
  });
};

export const flattenNavigationItems = (items: Navigation.AnyMenuItem[]) => items.flatMap(item => [item, ...(item.children ?? [])]);
export const flattenLookbookNavigationItems = (items: Lookbook.NavigationItem[]) => items.flatMap(item => [item, ...(item.children ?? [])]);
export const getIsPageMenuItem = (item: Navigation.AnyMenuItem): item is Navigation.PageMenuItem => item.type === 'page';

export const getIsLinkMenuItem = (item: Navigation.AnyMenuItem): item is Navigation.LinkMenuItem => item.type === 'link';

export const getIsFolderMenuItem = (item: Navigation.AnyMenuItem): item is Navigation.FolderMenuItem => item.type === 'folder';

export const getHasCurrentMarket = (item: Navigation.AnyMenuItem, market: Id) =>
  (getIsPageMenuItem(item) || getIsLinkMenuItem(item)) && item.markets?.includes(market);

interface BasePage {
  isHomepage: boolean;
  label: string;
  url: string;
}

export const getPagesListForOrderShare = (
  navigationItems: Navigation.UserMenuItem[],
  homepageSlug: Maybe<string>,
  basePages: BasePage[],
): BasePage[] => {
  const pages: BasePage[] = navigationItems
    .filter(page => page.type === 'page')
    .map(({ label, url }) => ({
      isHomepage: false,
      label,
      url: url!,
    }));
  const navigationPages = pages.reduce(
    (
      acc: {
        homepage: BasePage;
        pages: BasePage[];
      },
      cur,
    ) => (cur.url !== homepageSlug ? { ...acc, pages: [...acc.pages, cur] } : { ...acc, homepage: { ...cur, isHomepage: true } }),
    {
      // eslint-disable-next-line no-warning-comments
      // TODO check if using null here might be better
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, no-warning-comments
      homepage: {} as BasePage,
      pages: [],
    },
  );

  return [...(isEmpty(navigationPages.homepage) ? [] : [navigationPages.homepage]), ...basePages, ...navigationPages.pages];
};
